import type { List } from 'immutable';
import { LOADED_STATES } from 'pages/SnapshotManagement/constants';
import { makeSelectAllSnapshots } from 'pages/SnapshotManagement/selectors';
import type {
  ImmutableSnapshot,
  Snapshot,
  SnapshotStatus,
} from 'pages/SnapshotManagement/snapshotManagementTypes';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

const IN_PROGRESS_STATUSES: ReadonlySet<SnapshotStatus> = new Set([
  'ready',
  'run',
  'finishing',
]);

export const SNAPSHOT_IN_PROGRESS_LABEL_MESSAGE =
  'Labels cannot be changed while this snapshot is still being discovered.';

interface SelectorProps {
  snapshots: List<ImmutableSnapshot>;
}

const selector = createStructuredSelector({
  snapshots: makeSelectAllSnapshots(),
});

/**
 * All snapshots known to the store, including the unloaded ones, so that
 * `isSnapshotInProgress` can resolve any selected snapshot.
 */
const useAllSnapshots = (): Snapshot[] => {
  const { snapshots } = useSelector<unknown, SelectorProps>(selector);

  return useMemo(() => {
    const plainSnapshots = snapshots
      .map((snapshot) => snapshot?.toJS())
      .filter((snapshot) => snapshot !== undefined)
      .toArray();
    return plainSnapshots as Exclude<
      (typeof plainSnapshots)[number],
      undefined
    >[];
  }, [snapshots]);
};

/**
 * An unloaded snapshot is reported with its `finishStatus`, so it arrives here
 * as `done` as well. Only the loaded ones can win the comparison, the same way
 * the backend resolves `$last` from the stored status, which unloading rewrites.
 */
const canBeLatestSnapshot = (snapshot: Snapshot): boolean =>
  snapshot.status === 'done' && LOADED_STATES.includes(snapshot.state);

export const isLatestSnapshotId = (
  snapshots: Snapshot[],
  snapshotId: string | null,
): boolean => {
  if (snapshotId === null) {
    return false;
  }

  const latest = snapshots
    .filter(canBeLatestSnapshot)
    .sort((a, b) => b.tsStart - a.tsStart)[0];

  return latest?.id === snapshotId;
};

export const isSnapshotInProgress = (
  snapshots: Snapshot[],
  snapshotId: string | null,
): boolean => {
  if (snapshotId === null) {
    return false;
  }

  const selected = snapshots.find((snapshot) => snapshot.id === snapshotId);
  return selected !== undefined && IN_PROGRESS_STATUSES.has(selected.status);
};

export const useIsLatestSnapshot = (snapshotId: string | null): boolean =>
  isLatestSnapshotId(useAllSnapshots(), snapshotId);

export const useIsSnapshotInProgress = (snapshotId: string | null): boolean =>
  isSnapshotInProgress(useAllSnapshots(), snapshotId);
